import React from 'react'
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Container,
} from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { ExpandMore } from '@mui/icons-material'
import NavigationGroupLink from './NavigationGroupLink'
import { NavigationGroup as NavigationGroupType } from '../../types/page'

interface Props {
  navGroup: NavigationGroupType
  accordionClass?: string
  accordionClasses?: {
    root?: string
    expandIconWrapper?: string
    accordionSummary?: string
    accordionSummaryContent?: string
    accordionSummaryExpanded?: string
    accordionDetails?: string
    accordionDetailEntry?: string
    accEntryLink?: string
  }
  expanded?: string | false
  handleChange?: (s: string) => void
}

const NavigationGroupAccordionDesktop: React.FC<Props> = ({
  navGroup,
  accordionClass,
  accordionClasses,
  expanded,
  handleChange,
}) => {
  const { classes, cx } = useStyles()
  return (
    <Box onMouseLeave={handleChange ? () => handleChange('') : () => null}>
      <Accordion
        expanded={expanded === `accordion-details-${navGroup.contentful_id}`}
        onMouseOver={() =>
          handleChange?.(`accordion-details-${navGroup.contentful_id}`)
        }
        classes={{
          root: cx(classes.accordion, accordionClass, accordionClasses?.root),
          expanded: classes.accordionExpanded,
        }}
      >
        <AccordionSummary
          aria-controls={`accordion-details-${navGroup.contentful_id}`}
          classes={{
            root: cx(
              classes.accordionSummary,
              accordionClasses?.accordionSummary
            ),
            content: classes.accordionSummaryContent,
            expanded: cx(
              classes.accordionSummaryExpanded,
              accordionClasses?.accordionSummaryExpanded
            ),
            expandIconWrapper: classes.expandIconWrapper,
          }}
          expandIcon={
            <ExpandMore
              className={cx(classes.icon, accordionClasses?.expandIconWrapper)}
            />
          }
        >
          {navGroup.link ? (
            <NavigationGroupLink link={navGroup.link} />
          ) : (
            navGroup.title
          )}
        </AccordionSummary>
        <AccordionDetails
          className={cx(
            classes.accordionDetails,
            accordionClasses?.accordionDetails
          )}
          id={`accordion-details-${navGroup.contentful_id}`}
        >
          {navGroup.links.map((link) => (
            <Container
              disableGutters
              className={cx(
                classes.linkContainer,
                accordionClasses?.accordionDetailEntry
              )}
              key={link.contentful_id}
            >
              <NavigationGroupLink
                link={link}
                className={accordionClasses?.accEntryLink}
              />
            </Container>
          ))}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  accordion: {
    backgroundColor: theme.palette.background.default,
    margin: '0',

    '&:before': {
      display: 'none',
    },
    '& *': {
      '&:focus': {
        outline: 'none',
      },
    },
    [theme.breakpoints.down('lg')]: {
      padding: '0 6% 0 6%',
    },
  },
  accordionDetails: {
    backgroundColor: theme.palette.background.default,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    flexDirection: 'column',
    padding: '1rem 0',
    position: 'absolute',
    minWidth: '10em',
    top: '4.5em',
    [theme.breakpoints.down('lg')]: {
      borderTop: 'none',
      padding: '0 0 .54em',
      position: 'unset',
      width: '100%',
    },
  },
  accordionExpanded: {
    margin: '0 !important',
  },
  accordionSummary: {
    margin: '0',
    padding: '0',
    minHeight: '0',

    [theme.breakpoints.down('lg')]: {
      borderTop: `1px solid ${theme.palette.divider}`,
      minHeight: '0',
    },
  },
  accordionSummaryContent: {
    '&&': {
      margin: '1.4em 0',

      [theme.breakpoints.down('lg')]: {
        fontSize: '1.13rem',
        margin: 0,
        padding: '1.11em 0',
      },
    },
  },
  accordionSummaryExpanded: {
    minHeight: '0 !important',
  },
  expandIconWrapper: {
    padding: '.1em',
    marginRight: 0,
  },
  icon: {
    color: theme.palette.text.primary,
    fontSize: '1.25rem',
  },
  linkContainer: {
    fontSize: '.94rem',
    lineHeight: '1.53em',
    padding: '0.4rem 1.25rem',

    '&& a': {
      color: `${theme.palette.text.secondary}`,
    },

    [theme.breakpoints.down('lg')]: {
      padding: '.5em 0',
    },
  },
}))

export default NavigationGroupAccordionDesktop
